<template>
  <div class="account-page">
    <div
      class="account-page__inner"
      :class="{ 'account-page__inner--guest': guestView }"
    >
      <div v-if="!guestView" class="account-page__mobile-nav">
        <AccountNav :logging-out="loggingOut" @logout="logout" />
      </div>
      <aside v-if="!guestView" class="account-page__sidebar">
        <p class="account-page__eyebrow">My account</p>
        <AccountNav :logging-out="loggingOut" @logout="logout" />
      </aside>
      <section
        class="account-page__content"
        aria-label="Account content"
        tabindex="-1"
      >
        <div
          v-if="!clientReady || authState === 'checking'"
          class="account-page__status"
          role="status"
          aria-live="polite"
        >
          <span class="account-page__skeleton" aria-hidden="true"></span>
          <h1>My account</h1>
          <strong>Checking your account…</strong>
          <span>Your account navigation and content will stay in place.</span>
        </div>
        <slot v-else-if="authState === 'authenticated' || allowGuest" />
        <div
          v-else
          class="account-page__status"
          role="status"
          aria-live="polite"
        >
          <h1>My account</h1>
          <strong>Returning you to sign in…</strong>
        </div>
        <p class="account-page__live" role="status" aria-live="polite">
          {{ logoutStatus }}
        </p>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import AccountNav from "./AccountNav.vue";

const store = useStore();
const router = useRouter();
const props = withDefaults(defineProps<{ allowGuest?: boolean }>(), {
  allowGuest: false,
});
const clientReady = ref(false);
const loggingOut = ref(false);
const logoutStatus = ref("");
const authState = computed(
  () => store.getters["common/customer/authState"] || "checking"
);
const allowGuest = computed(() => props.allowGuest);
const guestView = computed(
  () => allowGuest.value && authState.value === "anonymous"
);

onMounted(() => {
  clientReady.value = true;
});

async function logout() {
  if (loggingOut.value) return;
  loggingOut.value = true;
  logoutStatus.value = "Signing out…";
  try {
    await store.dispatch("common/customer/logout");
    logoutStatus.value = "You’re signed out.";
    await router.replace("/account/login");
  } finally {
    loggingOut.value = false;
  }
}
</script>

<style scoped>
.account-page {
  min-width: 0;
  padding: clamp(1.5rem, 4vw, 4rem) clamp(1rem, 4vw, 3rem);
  background-color: #e2e0dc;
  background-image: url("/image/account/hooligans-eyewear-pattern-seamless-v3.png");
  background-position: center;
  background-size: clamp(14rem, 25vw, 22rem);
  background-repeat: repeat;
  color: #071c39;
}
.account-page__inner {
  display: grid;
  grid-template-columns: minmax(180px, 240px) minmax(0, 760px);
  gap: clamp(1.5rem, 4vw, 4rem);
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
}
.account-page__inner--guest {
  grid-template-columns: minmax(0, 960px);
  justify-content: center;
}
.account-page__sidebar {
  min-width: 0;
  align-self: start;
  padding: 1rem;
  border: 1px solid #d8d6cf;
  border-radius: 0.4rem;
  background: #fff;
}
.account-page__eyebrow {
  margin: 0 0 0.75rem;
  color: #525c68;
  font-size: 0.8rem;
  font-weight: 750;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.account-page__mobile-nav {
  display: none;
}
.account-page__content {
  min-width: 0;
  outline: none;
}
.account-page__content:focus-visible {
  outline: 3px solid #ee7203;
  outline-offset: 4px;
}
.account-page__status {
  display: grid;
  gap: 0.65rem;
  min-height: 220px;
  align-content: center;
  padding: 1.25rem;
  border: 1px solid #d8d6cf;
  border-radius: 0.4rem;
  background: #fff;
}
.account-page__status h1 {
  margin: 0;
  font-size: clamp(2rem, 5vw, 3rem);
}
.account-page__skeleton {
  display: block;
  width: min(100%, 18rem);
  height: 1rem;
  border-radius: 999px;
  background: #e8e6df;
}
.account-page__live {
  min-height: 1.5rem;
  margin: 0.75rem 0 0;
}
@media (max-width: 767px) {
  .account-page {
    padding: 1rem;
  }
  .account-page__inner {
    grid-template-columns: minmax(0, 1fr);
    gap: 1.25rem;
  }
  .account-page__sidebar {
    display: none;
  }
  .account-page__mobile-nav {
    display: block;
    min-width: 0;
    padding: 0.4rem;
    border: 1px solid #d8d6cf;
    border-radius: 0.4rem;
    background: #fff;
  }
}
@media (prefers-reduced-motion: reduce) {
  .account-page *,
  .account-page *::before,
  .account-page *::after {
    scroll-behavior: auto !important;
    transition: none !important;
  }
}
</style>
